import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Select, type SelectOptionRendererProps } from '@tractian/ui'

import {
  HUBSPOT_SEARCH_MIN_QUERY_LENGTH,
  useHubspotSearch,
  type HubspotSearchItem,
  type HubspotSearchItemType
} from '@/features/assessments/api/hubspot-search'
import { HubspotSearchOption } from '@/features/assessments/components/hubspot-search-option'
import { useDebouncedValue } from '@/features/plant-assessment/hooks/use-debounced-value'
import {
  INLINE_CELL_SELECT_VALUE_CLASS,
  inlineCellSelectRenderTag,
  preventCellBlurOnPointerDown
} from '@/features/plant-assessment/utils/inline-cell-select-styles'

export const HUBSPOT_PICKER_DEBOUNCE_MS = 250

// Two-line option (title row + owners/address row).
const OPTION_HEIGHT = 52

type HubspotSelectOption = {
  value: string
  label: string
  item: HubspotSearchItem
}

export type HubspotPickerProps = {
  value: HubspotSearchItem | null
  onChange: (item: HubspotSearchItem | null) => void
  // Restricts the search to deals or plants; both when unset.
  type?: HubspotSearchItemType
  disabled?: boolean
  autoFocus?: boolean
  error?: boolean
  // Accessible name of the trigger; defaults to "HubSpot Deal or Plant".
  label?: string
  placeholder?: string
  // Controlled popover state; inline grid editors use it to open on mount.
  open?: boolean
  onOpenChange?: (open: boolean) => void
  // Ag-grid cell editor presentation: the whole cell reads as the field.
  inline?: boolean
}

function toOption(item: HubspotSearchItem): HubspotSelectOption {
  return { value: item.id, label: item.title, item }
}

function HubspotSelectOptionRow({
  option,
  ...props
}: SelectOptionRendererProps<HubspotSelectOption>) {
  return (
    <Select.Option option={option} {...props}>
      <HubspotSearchOption item={option.item} className="flex-1 px-0 py-0" />
      <Select.Option.Icon />
    </Select.Option>
  )
}

export function HubspotPicker({
  value,
  onChange,
  type,
  disabled = false,
  autoFocus = false,
  error = false,
  label,
  placeholder,
  open,
  onOpenChange,
  inline = false
}: HubspotPickerProps) {
  const { t } = useTranslation('plantAssessment')
  const [query, setQuery] = useState('')

  const debouncedQuery = useDebouncedValue(query, HUBSPOT_PICKER_DEBOUNCE_MS)
  const search = useHubspotSearch(debouncedQuery, type)

  const tooShort = query.trim().length < HUBSPOT_SEARCH_MIN_QUERY_LENGTH
  // Typed text not yet sent counts as loading, so "no results" never flashes.
  const pending = query.trim() !== debouncedQuery.trim() || search.isFetching
  const items = tooShort || search.isError ? [] : (search.data ?? [])
  // The current pick stays an option so the trigger keeps showing it while a
  // new search no longer returns it.
  const options = items.map(toOption)
  if (value && !items.some(item => item.id === value.id)) options.unshift(toOption(value))

  function handleValueChange(next: string | string[] | null) {
    if (Array.isArray(next)) return
    onChange(options.find(option => option.value === next)?.item ?? null)
  }

  return (
    <Select
      options={options}
      value={value?.id ?? null}
      onValueChange={handleValueChange}
      showSearch
      search={query}
      onSearchChange={setQuery}
      matchSearch={() => true}
      disabled={disabled}
      error={error}
      open={open}
      onOpenChange={onOpenChange}
    >
      <Select.Trigger autoFocus={autoFocus} aria-label={label ?? t('hubspotPicker.label')}>
        <Select.Value
          placeholder={placeholder ?? t('hubspotPicker.placeholder')}
          allowClear
          className={inline ? INLINE_CELL_SELECT_VALUE_CLASS : undefined}
          renderTag={inline ? inlineCellSelectRenderTag : undefined}
        />
      </Select.Trigger>
      <Select.Portal>
        <Select.Content onMouseDown={inline ? preventCellBlurOnPointerDown : undefined}>
          <Select.Options
            renderOption={HubspotSelectOptionRow}
            estimateSize={() => OPTION_HEIGHT}
          />
          <Select.Empty>
            <EmptyState
              tooShort={tooShort}
              isError={search.isError}
              pending={pending}
              type={type}
              onRetry={() => void search.refetch()}
            />
          </Select.Empty>
        </Select.Content>
      </Select.Portal>
    </Select>
  )
}

type EmptyStateProps = {
  tooShort: boolean
  isError: boolean
  pending: boolean
  type?: HubspotSearchItemType
  onRetry: () => void
}

// What the list shows when it has no options: hint, error, loading or empty.
function EmptyState({ tooShort, isError, pending, type, onRetry }: EmptyStateProps) {
  const { t } = useTranslation('plantAssessment')

  if (tooShort) return t('hubspotPicker.minChars')
  if (isError) {
    return (
      <span role="alert" className="flex items-center gap-2 text-red-600">
        {t('hubspotPicker.error')}
        <Button type="button" variant="link" size="small" onClick={onRetry}>
          {t('hubspotPicker.retry')}
        </Button>
      </span>
    )
  }
  if (pending) return t('hubspotPicker.loading')
  if (type === 'plant') return t('hubspotPicker.emptyPlants')
  return t('hubspotPicker.empty')
}
